'use client';
import { filterUndefined, arrayOf, isFunction } from '@s7/utils';
import { defaultPropObject } from '../../constants.js';
import { createMemoizedEmittersEvaluator } from './createMemoizedEmittersEvaluator.js';
import { useCSS } from './core/css/useCSS.js';
import { mergeStyles } from '../mergeStyles/mergeStyles.js';
import { useNodeOverridableTheme, useEmotionCache } from '../../theming/propagation/ThemeProvider.js';

function _array_like_to_array(arr, len) {
    if (len == null || len > arr.length) len = arr.length;
    for(var i = 0, arr2 = new Array(len); i < len; i++)arr2[i] = arr[i];
    return arr2;
}
function _extends() {
    _extends = Object.assign || function(target) {
        for(var i = 1; i < arguments.length; i++){
            var source = arguments[i];
            for(var key in source){
                if (Object.prototype.hasOwnProperty.call(source, key)) {
                    target[key] = source[key];
                }
            }
        }
        return target;
    };
    return _extends.apply(this, arguments);
}
function _unsupported_iterable_to_array(o, minLen) {
    if (!o) return;
    if (typeof o === "string") return _array_like_to_array(o, minLen);
    var n = Object.prototype.toString.call(o).slice(8, -1);
    if (n === "Object" && o.constructor) n = o.constructor.name;
    if (n === "Map" || n === "Set") return Array.from(n);
    if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _array_like_to_array(o, minLen);
}
function _create_for_of_iterator_helper_loose(o, allowArrayLike) {
    var it = typeof Symbol !== "undefined" && o[Symbol.iterator] || o["@@iterator"];
    if (it) return (it = it.call(o)).next.bind(it);
    if (Array.isArray(o) || (it = _unsupported_iterable_to_array(o)) || allowArrayLike && o && typeof o.length === "number") {
        if (it) o = it;
        var i = 0;
        return function() {
            if (i >= o.length) {
                return {
                    done: true
                };
            }
            return {
                done: false,
                value: o[i++]
            };
        };
    }
    throw new TypeError("Invalid attempt to iterate non-iterable instance.\\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}
var createStyles = function(emitter) {
    var getBaseStyles = createMemoizedEmittersEvaluator(emitter);
    return function(input) {
        var _loop = function(key1) {
            /*
             * Добавляет классы, соответствующие статическим селекторам.
             * Из-за использования: `name: ['Component', __staticSelector]`
             * необходимо отфильтровать undefined.
             * */ var staticSelector = componentId.map(function(name) {
                return name ? cache.key + "-" + name + "-" + key1 : "";
            }).join(" ");
            classes[key1] = cx(/*
                 * Отображаем CSSObject в имена классов, добавляя стили в кэш с помощью css.
                 * Генерация основных стилей, кэшированием занимается emotion.
                 * */ css(cssObjects[key1]), /*
                 * Дополнительные имена классов
                 * */ classNamesOverride[key1], classNames[key1], staticSelector);
        };
        var _ref = input || {}, _ref_params = _ref.params, params = _ref_params === void 0 ? defaultPropObject : _ref_params, options = _ref.options, localTheme = _ref.theme;
        var _ref1 = options || {}, _ref_name = _ref1.name, name = _ref_name === void 0 ? [] : _ref_name, _ref_classNames = _ref1.classNames, classNames = _ref_classNames === void 0 ? defaultPropObject : _ref_classNames, _ref_variations = _ref1.variations, variations = _ref_variations === void 0 ? defaultPropObject : _ref_variations, _ref_styles = _ref1.styles, styles = _ref_styles === void 0 ? defaultPropObject : _ref_styles;
        filterUndefined(params);
        var theme = useNodeOverridableTheme(localTheme);
        var cache = useEmotionCache();
        var _useCSS = useCSS(), css = _useCSS.css, cx = _useCSS.cx;
        /*
         * Накапливает переопределяемые стили;
         *
         * Выполнение поверхностного копирования:
         *
         * Необходимость:
         * Требуется, чтобы корректно применить дальнейшие переопределения
         * (то есть только для одного прецедента использования).
         *
         * Достаточность:
         * В силу того, что mergeStyles выполняет поверхностное копирование объектов,
         * поверхностного копирования здесь достаточно.
         * */ var cssObjects = _extends({}, getBaseStyles(theme, params, variations));
        var inject = function(emitter) {
            return mergeStyles(cssObjects, isFunction(emitter) ? emitter(theme, params, variations) : emitter);
        };
        var componentId = arrayOf(name);
        var overrides = componentId.map(function(name) {
            return name && theme.components[name];
        });
        var classNamesOverride = {};
        /*
         * Порядок, в котором вызываются assignAdjacentStyles определяет приоритет
         * одних стилей над другими.
         * */ for(var _iterator = _create_for_of_iterator_helper_loose(overrides), _step; !(_step = _iterator()).done;){
            var override = _step.value;
            if (!override) {
                continue;
            }
            var styles1 = override.styles, variants = override.variants, sizes = override.sizes, classNames1 = override.classNames;
            var size = variations.size, variant = variations.variant;
            /*
             * Добавляет стили, вычисляемые на основе `variants` из темы;
             * */ if (variants && variant && variant in variants) {
                inject(variants[variant]);
            }
            /*
             * Добавляет стили, вычисляемые на основе `sizes` из темы;
             * */ if (sizes && size && size in sizes) {
                inject(sizes[size]);
            }
            /*
             * Добавляет стили, вычисляемые на основе `styles` из темы;
             * */ if (styles1) {
                inject(styles1);
            }
            if (classNames1) {
                for(var key in classNames1){
                    var value = classNames1[key];
                    if (typeof classNamesOverride[key] !== "string") {
                        classNamesOverride[key] = value;
                    } else {
                        classNamesOverride[key] += " " + value;
                    }
                }
            }
        }
        /*
         * Добавляет стили, вычисляемые на основе `styles`, определённых на уровне компонента;
         * */ inject(styles);
        var classes = {};
        for(var key1 in cssObjects)_loop(key1);
        return {
            classes: classes,
            cx: cx,
            theme: theme
        };
    };
};

export { createStyles };
